import React from 'react'
import { useRouter } from 'next/router'

import DiffCard from 'components/comparePage/DiffCard'

import { DeckResponse } from 'services/apiTypes/deck.types'
import { Diff, Diffable, DiffableFields, QueryParams } from 'components/comparePage/utils'

import styles from './cardComparison.module.scss'

type Props = {
  diff: Diff
  deckA: DeckResponse
  deckB: DeckResponse
}

const CardComparison = ({ diff, deckA, deckB }: Props) => {
  const { aDiff, bDiff, differentFields } = diff

  return (
    <div className={styles.container}>
      <CardComparisonHalf diffable={aDiff} differentFields={differentFields} deckA={true} otherDeck={deckB} />
      <CardComparisonHalf diffable={bDiff} differentFields={differentFields} deckA={false} otherDeck={deckA} />
    </div>
  )
}

export default CardComparison

type ComparisonHalfProps = {
  diffable: Diffable
  differentFields: DiffableFields[]
  deckA: boolean
  otherDeck: DeckResponse
}

const CardComparisonHalf = ({ deckA, otherDeck, diffable, differentFields }: ComparisonHalfProps) => {
  const card = diffable.records[0]

  const router = useRouter()
  const { diffFields } = router.query as QueryParams // There may be more than just order by on the URL, so be sure to always update the URL with all of it's params

  const enabledDiffFields = (diffFields ? diffFields?.split(',') : []) as DiffableFields[]

  // If no diff fields are supplied all are used
  const showTotalQuantity = diffFields ? enabledDiffFields.includes('totalQuantity') : true
  const showTotalEditions = diffFields ? enabledDiffFields.includes('totalEditions') : true
  const showTotalFinishes = diffFields ? enabledDiffFields.includes('totalFinishes') : true
  const showTotalCustomCmc = diffFields ? enabledDiffFields.includes('totalCustomCmc') : true
  const showTotalCompanion = diffFields ? enabledDiffFields.includes('totalCompanion') : true
  const showTotalColorLabel = diffFields ? enabledDiffFields.includes('totalColorLabel') : true
  const showTotalCategories = diffFields ? enabledDiffFields.includes('totalCategories') : true

  // Formatting all of the totals used in the diff. We're pushing everything through sets to only show unique values
  const quantity = diffable.totalQuantity
  const modifiers = [...new Set(diffable.totalFinishes)].join(', ')
  const categories = [...new Set(diffable.totalCategories)].join(', ')
  const customCmc = [
    ...new Set(diffable.totalCustomCmc.map(customCmcValue => (customCmcValue === 'null' ? 'N/A' : customCmcValue))),
  ].join(', ')

  const companion = [
    ...new Set(diffable.totalCompanion.map(companionValue => (companionValue === 'true' ? 'Yes' : 'No'))),
  ].join(', ')

  const editions = [
    ...new Set(
      diffable.totalEditions.map(
        (_e, index) => `${diffable.records[index].setCode} (${diffable.records[index].collectorNumber})`,
      ),
    ),
  ].join(', ')

  const colorLabels = [...new Set(diffable.totalColorLabel.map(colorLabel => colorLabel))].map(colorLabel =>
    JSON.parse(colorLabel),
  )

  return (
    <div className={styles.comparisonCardContainer}>
      <div className={styles.cardWrapper}>
        <DiffCard className={styles.card} deckA={deckA} card={card} otherDeck={otherDeck} />
        {diffable.records.length > 1 && <div className={styles.quantityTag}>+{diffable.totalQuantity - card.qty}</div>}
      </div>
      <div className={styles.fields}>
        {showTotalQuantity && (
          <div className={`${!differentFields.includes('totalQuantity') && styles.dimmed}`}>Quantity: {quantity}</div>
        )}
        {showTotalFinishes && (
          <div className={`${!differentFields.includes('totalFinishes') && styles.dimmed}`}>Finish: {modifiers}</div>
        )}
        {showTotalEditions && (
          <div className={`${!differentFields.includes('totalEditions') && styles.dimmed}`}>Printing: {editions}</div>
        )}
        {showTotalCustomCmc && (
          <div className={`${!differentFields.includes('totalCustomCmc') && styles.dimmed}`}>
            Custom CMC: {customCmc}
          </div>
        )}
        {showTotalCompanion && (
          <div className={`${!differentFields.includes('totalCompanion') && styles.dimmed}`}>
            Companion: {companion}
          </div>
        )}
        {showTotalColorLabel && (
          <div className={`${!differentFields.includes('totalColorLabel') && styles.dimmed}`}>
            Color Label:{' '}
            {colorLabels.map(({ name, color }, index) => (
              <React.Fragment key={index}>
                <Splotch name={name} color={color} />
                {index === colorLabels.length - 1 ? '' : ', '}
              </React.Fragment>
            ))}
          </div>
        )}
        {showTotalCategories && (
          <div className={`${!differentFields.includes('totalCategories') && styles.dimmed}`}>
            Categories: {categories}
          </div>
        )}
      </div>
    </div>
  )
}

const Splotch = ({ name, color }: { name: string; color: string }) => (
  <>
    <span className={styles.splotch} style={{ backgroundColor: color }} />
    {name || 'Default'}
  </>
)
